import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getReviews } from "../../helpers/fakebackend_helper";

// Unified App Store + Play Store reviews feed, extracted from the old standalone
// Reviews page so it can live under the App Metrics "Daily downloads" board.
// Backend returns both sources merged + sorted by date desc; we filter by
// platform client-side so toggling is instant (no refetch).

const PLATFORM_META = {
  ios: {
    label: "App Store",
    short: "iOS",
    icon: "ri-apple-fill",
    tint: { bg: "rgba(255,255,255,0.08)", fg: "#e6e7ee", dot: "#d6d7df" },
  },
  android: {
    label: "Google Play",
    short: "Android",
    icon: "ri-google-play-fill",
    tint: { bg: "rgba(0,205,82,0.14)", fg: "#7be09a", dot: "#2BD56F" },
  },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "ios", label: "App Store" },
  { key: "android", label: "Google Play" },
];

const errMessage = (err) =>
  typeof err === "string"
    ? err
    : err?.response?.data?.message || err?.message || "Unknown error";

function formatDate(input) {
  if (!input) return "—";
  const ts = new Date(input).getTime();
  if (!Number.isFinite(ts)) return "—";
  return new Date(ts).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const Stars = ({ rating }) => {
  const r = Math.round(Number(rating) || 0);
  return (
    <span style={{ color: "#f3af4c", letterSpacing: 1, fontSize: "0.95rem" }} aria-label={`${r} of 5 stars`}>
      {"★".repeat(r)}
      <span style={{ color: "rgba(255,255,255,0.22)" }}>{"★".repeat(Math.max(0, 5 - r))}</span>
    </span>
  );
};

const PlatformBadge = ({ platform }) => {
  const meta = PLATFORM_META[platform];
  if (!meta) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        background: meta.tint.bg,
        color: meta.tint.fg,
        fontSize: "0.72rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <i className={meta.icon} style={{ fontSize: "0.95rem" }} />
      {meta.label}
    </span>
  );
};

const ReviewCard = ({ review }) => (
  <div
    className="bl-glass"
    style={{
      padding: "1.1rem 1.25rem",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      position: "relative",
    }}
  >
    {/* Platform badge — top right of each review */}
    <div style={{ position: "absolute", top: 14, right: 14 }}>
      <PlatformBadge platform={review.platform} />
    </div>

    <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 120 }}>
      <Stars rating={review.rating} />
      {review.version && (
        <span style={{ color: "#9a9caa", fontSize: "0.72rem" }}>v{review.version}</span>
      )}
    </div>

    {review.title && (
      <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.98rem", paddingRight: 120 }}>
        {review.title}
      </div>
    )}

    {review.body && (
      <div style={{ color: "#c9cad6", fontSize: "0.9rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
        {review.body}
      </div>
    )}

    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#9a9caa", fontSize: "0.78rem", marginTop: 2 }}>
      <span style={{ fontWeight: 600, color: "#b3b6c4" }}>{review.author || "Anonymous"}</span>
      <span>·</span>
      <span>{formatDate(review.date)}</span>
      {review.territory && (
        <>
          <span>·</span>
          <span style={{ textTransform: "uppercase" }}>{review.territory}</span>
        </>
      )}
    </div>
  </div>
);

const FilterTab = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: "6px 14px",
      borderRadius: 999,
      border: "1px solid",
      borderColor: active ? "rgba(243,175,76,0.6)" : "rgba(255,255,255,0.12)",
      background: active ? "rgba(243,175,76,0.16)" : "transparent",
      color: active ? "#ffd199" : "#b3b6c4",
      fontSize: "0.82rem",
      fontWeight: 600,
      cursor: "pointer",
    }}
  >
    {label}
    {count != null && (
      <span style={{ opacity: 0.7, marginLeft: 6 }}>{count}</span>
    )}
  </button>
);

const ReviewsFeed = () => {
  const [reviews, setReviews] = useState([]);
  const [sources, setSources] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await getReviews({ platform: "all", limit: 100 });
      setReviews(Array.isArray(body?.reviews) ? body.reviews : []);
      setSources(body?.sources || {});
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { all: reviews.length, ios: 0, android: 0 };
    for (const r of reviews) {
      if (r.platform === "ios") c.ios += 1;
      else if (r.platform === "android") c.android += 1;
    }
    return c;
  }, [reviews]);

  const visible = useMemo(
    () => (filter === "all" ? reviews : reviews.filter((r) => r.platform === filter)),
    [reviews, filter]
  );

  // Surface per-source problems (e.g. a store not configured) without hiding the
  // other store's reviews.
  const sourceWarnings = Object.entries(sources)
    .filter(([, v]) => v && v.ok === false)
    .map(([k, v]) => ({ platform: k, error: v.error }));

  return (
    <div className="bl-glass p-6 shadow-sm">
      {/* Header / controls */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1.25rem",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <h3 className="text-lg font-medium text-gray-100" style={{ margin: 0 }}>
            App Reviews
          </h3>
          <div style={{ color: "#9a9caa", fontSize: "0.85rem", marginTop: 4 }}>
            Unified feed across Apple App Store and Google Play.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {FILTERS.map((f) => (
            <FilterTab
              key={f.key}
              active={filter === f.key}
              label={f.label}
              count={counts[f.key]}
              onClick={() => setFilter(f.key)}
            />
          ))}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "transparent",
              color: "#b3b6c4",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <i className="ri-refresh-line" style={{ marginRight: 6 }} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {sourceWarnings.length > 0 && (
        <div
          style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: 12,
            background: "rgba(243,175,76,0.08)",
            border: "1px solid rgba(243,175,76,0.2)",
            color: "#ffd199",
            fontSize: "0.82rem",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {sourceWarnings.map((w) => (
            <div key={w.platform}>
              <strong style={{ textTransform: "capitalize" }}>
                {PLATFORM_META[w.platform]?.label || w.platform}:
              </strong>{" "}
              {w.error || "unavailable"}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ padding: "1rem 1.25rem", color: "#ff8a98", fontSize: "0.9rem" }}>
          Failed to load reviews: {error}
        </div>
      )}

      {!error && !loading && visible.length === 0 && (
        <div style={{ padding: "2rem 1.25rem", color: "#9a9caa", textAlign: "center" }}>
          No reviews to show.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {visible.map((r) => (
          <ReviewCard key={r.id} review={r} />
        ))}
      </div>
    </div>
  );
};

export default ReviewsFeed;
